import { formatShortDateTime } from '../../../shared/time';
import {
  createEmptyOnboardingPayload,
  type OnboardingIntakeFile,
  type OnboardingPayload,
} from '../../../types/onboardingIntake';
import {
  emptyDraft,
  type IntakeDraft,
  normalizeDraft,
  type StepKey,
} from './model';
import type { SuggestedContact } from './steps';

/**
 * A shared intake link (`/store-onboarding/<token>`) — pure logic.
 *
 * A group adds a location by sending this link. The answers live on the
 * server (autosaved), it may start from another location's answers
 * (`copied`: payload path → the value that was copied), and each step is
 * confirmed before the next.
 */

export type LinkState =
  | 'open'
  | 'submitted'
  | 'approved'
  | 'revoked'
  | 'expired';

export interface Confirmation {
  at: string;
  name: string;
}

export interface LinkView {
  state: LinkState;
  location_name: string;
  group_name: string;
  copied_from_name: string;
  same_legal_entity: boolean;
  payload?: Partial<Record<keyof OnboardingPayload, unknown>>;
  copied?: Record<string, unknown>;
  draft_state?: Partial<LinkDraftState>;
  confirmed_steps?: Partial<Record<StepKey, Confirmation>>;
  files?: OnboardingIntakeFile[];
  suggested_contacts?: SuggestedContact[];
  submitted_at?: string | null;
  expires_at?: string | null;
}

/** Where the person filling the link is; saved with the answers. */
export type LinkDraftState = Omit<IntakeDraft, 'payload' | 'updatedAt'>;

export const draftStateOf = ({
  payload: _payload,
  updatedAt: _updatedAt,
  ...rest
}: IntakeDraft): LinkDraftState => rest;

/** A server payload holds only the sections that were answered or copied. */
export function payloadFromServer(
  raw: LinkView['payload'] | undefined,
): OnboardingPayload {
  const empty = createEmptyOnboardingPayload();
  const out: Record<string, unknown> = { ...empty };
  for (const [key, value] of Object.entries(raw ?? {})) {
    const base = (empty as unknown as Record<string, unknown>)[key];
    out[key] =
      base && typeof base === 'object' && !Array.isArray(base) && value
        ? { ...base, ...(value as object) }
        : (value ?? base);
  }
  return out as unknown as OnboardingPayload;
}

const hasLegalParts = (p: OnboardingPayload) =>
  Boolean(p.location.legal_address_line_1 && p.location.legal_city);

export function draftFromView(view: LinkView): IntakeDraft {
  const payload = payloadFromServer(view.payload);
  const saved = view.draft_state ?? {};
  return normalizeDraft({
    ...emptyDraft(),
    // A legal address copied from the source store came from its lookup.
    legalAddressSelected: hasLegalParts(payload),
    ...saved,
    payload,
    updatedAt: null,
  });
}

// ---------------------------------------------------------------------------
// Copied answers
// ---------------------------------------------------------------------------

export function getPath(obj: unknown, path: string): unknown {
  return path
    .split('.')
    .reduce<unknown>(
      (node, part) =>
        node && typeof node === 'object'
          ? (node as Record<string, unknown>)[part]
          : undefined,
      obj,
    );
}

const norm = (v: unknown): unknown => {
  if (v === '' || v === null || v === undefined) return null;
  if (Array.isArray(v)) {
    const items = v.map(norm).filter((x) => x !== null);
    return items.length ? [...items].sort() : null;
  }
  return v;
};

export const sameAnswer = (a: unknown, b: unknown) =>
  JSON.stringify(norm(a)) === JSON.stringify(norm(b));

/** The payload path a field key stands for (address fields use a short key). */
const FIELD_PATH: Record<string, string> = {
  'location.legal': 'location.legal_business_address_raw',
};

export type CopiedMark =
  | { kind: 'copied' }
  | { kind: 'changed'; was: number | null };

/** "From Main St" when a field still holds the copied answer, "Changed" when edited. */
export function markFor(
  key: string,
  copied: Record<string, unknown>,
  payload: OnboardingPayload,
): CopiedMark | null {
  const path = FIELD_PATH[key] ?? key;
  if (!(path in copied)) return null;
  const was = copied[path];
  if (sameAnswer(getPath(payload, path), was)) return { kind: 'copied' };
  return { kind: 'changed', was: typeof was === 'number' ? was : null };
}

const SECTION_STEP: Record<string, StepKey> = {
  business: 'restaurant',
  location: 'restaurant',
  registration: 'restaurant',
  authorized_representative: 'people',
  primary_contact: 'people',
  ordering: 'orders',
  reservations: 'reservations',
  events: 'events',
  catering: 'events',
  gift_cards: 'faqs',
  store_faqs: 'faqs',
  knowledge_base: 'faqs',
};

const PATH_STEP: Record<string, StepKey> = {
  'business.services_used': 'services',
  'call_handling.human_transfer_enabled': 'services',
};

export const stepOfPath = (path: string): StepKey | undefined =>
  PATH_STEP[path] ?? SECTION_STEP[path.split('.')[0]];

/** Copied answers on a step, and how many of them were changed. */
export function copiedCounts(
  step: StepKey,
  copied: Record<string, unknown>,
  payload: OnboardingPayload,
): { copied: number; changed: number } {
  const paths = Object.keys(copied).filter((p) => stepOfPath(p) === step);
  return {
    copied: paths.length,
    changed: paths.filter((p) => !sameAnswer(getPath(payload, p), copied[p]))
      .length,
  };
}

export const stepsWithCopies = (copied: Record<string, unknown>) =>
  new Set(
    Object.keys(copied)
      .map(stepOfPath)
      .filter((s): s is StepKey => !!s),
  );

export const confirmationLabel = (c: Confirmation) =>
  c.name
    ? `Confirmed by ${c.name} · ${formatShortDateTime(c.at)}`
    : `Confirmed ${formatShortDateTime(c.at)}`;
